import { useCallback, useEffect, useRef, useState } from 'react';
import type { MonitoraggioWithPrice } from '@flymind/shared';
import { api } from '../lib/api-client';

interface ApiStats {
  activeMonitors: number;
  totalNotifications: number;
  estimatedSavings: number;
  planUsage: {
    currentMonitors: number;
    maxMonitors: number;
    plan: string;
    aiAnalysis: boolean;
    alertPredittivo: boolean;
  };
}

interface UserStats {
  monitoraggiAttivi: number;
  risparmioStimato: number;
  notificheNonLette: number;
}

export interface MonitorFilters {
  statoAlert?: string;
  attivo?: string;
  tipo?: string;
  search?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
}

interface PaginationInfo {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

interface UseMonitorsReturn {
  monitors: MonitoraggioWithPrice[];
  stats: UserStats | null;
  pagination: PaginationInfo | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  toggleActive: (id: string, attivo: boolean) => Promise<void>;
  deleteMonitor: (id: string) => Promise<void>;
}

function buildQueryString(filters: MonitorFilters): string {
  const params = new URLSearchParams();
  if (filters.statoAlert && filters.statoAlert !== 'tutti') {
    params.set('stato_alert', filters.statoAlert);
  }
  if (filters.attivo && filters.attivo !== 'tutti') {
    params.set('attivo', filters.attivo);
  }
  if (filters.tipo && filters.tipo !== 'tutti') {
    params.set('tipo', filters.tipo);
  }
  if (filters.page) {
    params.set('page', String(filters.page));
  }
  if (filters.pageSize) {
    params.set('pageSize', String(filters.pageSize));
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

function filtersKey(filters: MonitorFilters): string {
  return JSON.stringify(filters);
}

export function useMonitors(filters?: MonitorFilters): UseMonitorsReturn {
  const [monitors, setMonitors] = useState<MonitoraggioWithPrice[]>([]);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const qs = buildQueryString(filtersRef.current ?? {});
      const [monitorsData, statsData] = await Promise.all([
        api.get<{
          items: MonitoraggioWithPrice[];
          total: number;
          page: number;
          pageSize: number;
          totalPages: number;
          hasNext: boolean;
          hasPrevious: boolean;
        }>(`/monitoraggi${qs}`),
        api.get<ApiStats>('/users/me/stats'),
      ]);
      setMonitors(monitorsData.items ?? []);
      setPagination({
        total: monitorsData.total,
        page: monitorsData.page,
        pageSize: monitorsData.pageSize,
        totalPages: monitorsData.totalPages,
        hasNext: monitorsData.hasNext,
        hasPrevious: monitorsData.hasPrevious,
      });
      setStats({
        monitoraggiAttivi: statsData.activeMonitors ?? 0,
        risparmioStimato: statsData.estimatedSavings ?? 0,
        notificheNonLette: statsData.totalNotifications ?? 0,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore nel caricamento dei dati');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Re-fetch when filters change (serialized comparison)
  const serialized = filtersKey(filters ?? {});
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialized]);

  const toggleActive = useCallback(
    async (id: string, attivo: boolean) => {
      try {
        await api.patch(`/monitoraggi/${id}`, { attivo });
        setMonitors((prev) =>
          prev.map((m) => (m.id === id ? { ...m, attivo } : m)),
        );
        setStats((prev) =>
          prev
            ? {
                ...prev,
                monitoraggiAttivi: prev.monitoraggiAttivi + (attivo ? 1 : -1),
              }
            : prev,
        );
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Errore nell\'aggiornamento');
        throw err;
      }
    },
    [],
  );

  const deleteMonitor = useCallback(
    async (id: string) => {
      try {
        await api.delete(`/monitoraggi/${id}`);
        setMonitors((prev) => prev.filter((m) => m.id !== id));
        setStats((prev) =>
          prev
            ? {
                ...prev,
                monitoraggiAttivi: Math.max(0, prev.monitoraggiAttivi - 1),
              }
            : prev,
        );
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Errore nell\'eliminazione');
        throw err;
      }
    },
    [],
  );

  return {
    monitors,
    stats,
    pagination,
    isLoading,
    error,
    refetch: fetchData,
    toggleActive,
    deleteMonitor,
  };
}
